import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  contactsService,
  feedService,
  interestsService,
  blockService,
  chatService,
  ContactRecord,
  PlanInfo,
  PublicPhoto,
} from "@/services";
import { rdb } from "@/services/config";
import { onValue, ref } from "firebase/database";
import {
  AvatarFeed,
  InfoGroup,
  PhotoGallery,
  ShareModal,
  ModalAlert,
  UserStatus,
} from "@/components";
import { useUser } from "@/contexts";
import { usePermissions } from "@/hooks/usePermissions";
import { getAge } from "@/utils";
import "./FeedUser.css";

interface FeedUserData {
  uid: string;
  username?: string;
  birthYear?: string;
  city?: string;
  state?: string;
  gender?: string;
  profileType?: string;
  profileImage?: PublicPhoto | string;
  publicPhotos?: PublicPhoto[];
  bioTitle?: string;
  bioDescription?: string;
  profession?: string;
  education?: string;
  height?: string;
  bodyType?: string;
  skinTone?: string;
  hairColor?: string;
  eyeColor?: string;
  seeking?: string;
  children?: string;
  maritalStatus?: string;
  smoking?: string;
  drinking?: string;
  photosCount?: number;
  isVerified?: boolean;
  planInfo?: PlanInfo;
}

const FeedUserScreen: React.FC = () => {
  const { username } = useParams<{ username: string }>();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const { user: currentUser, userData } = useUser();

  const moreRef = useRef<HTMLDivElement | null>(null);

  const [user, setUser] = useState<FeedUserData | null>(null);
  const [presence, setPresence] = useState<any>(null);

  const [chatLoading, setChatLoading] = useState(false);

  const [isFavorited, setIsFavorited] = useState(false);
  const [favoriteLoading, setFavoriteLoading] = useState(false);

  const [viewRegistered, setViewRegistered] = useState(false);

  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(0);
  const [isSinglePhotoMode, setIsSinglePhotoMode] = useState(false);

  const [contacts, setContacts] = useState<ContactRecord[]>([]);

  const [isMoreOpen, setIsMoreOpen] = useState(false);

  const [isLoading, setIsLoading] = useState(true);
  const [blockLoading, setBlockLoading] = useState(false);

  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");

  const profileImageUrl = useMemo(() => {
    const img = user?.profileImage;
    if (!img) return null;

    if (typeof img === "string") {
      return img;
    }

    if (typeof img === "object" && img.approved !== false) {
      return img.url;
    }

    return null;
  }, [user?.profileImage]);

  const isOwnProfile = useMemo(
    () =>
      currentUser?.uid && user?.uid ? currentUser.uid === user.uid : false,
    [currentUser?.uid, user?.uid],
  );

  const age = useMemo(() => getAge(user?.birthYear), [user?.birthYear]);

  const galleryAllPhotos = useMemo(() => {
    const photos = (user?.publicPhotos || [])
      .map((p) => {
        if (typeof p === "string") return p;

        if (typeof p === "object" && p.url) return p.url;

        return null;
      })
      .filter((url): url is string => !!url);

    if (profileImageUrl && !photos.includes(profileImageUrl)) {
      return [profileImageUrl, ...photos];
    }
    return photos;
  }, [user?.publicPhotos, profileImageUrl]);

  const galleryOffset = useMemo(() => {
    const photosUrls = (user?.publicPhotos || []).map((p) =>
      typeof p === "string" ? p : p?.url,
    );
    return profileImageUrl && !photosUrls.includes(profileImageUrl) ? 1 : 0;
  }, [profileImageUrl, user?.publicPhotos]);

  const openGalleryAt = (index: number) => {
    if (!galleryAllPhotos.length) return;

    setIsSinglePhotoMode(index === 0);

    setGalleryIndex(Math.max(0, Math.min(index, galleryAllPhotos.length - 1)));
    setIsGalleryOpen(true);
  };

  const handleStartChat = async () => {
    if (!username || !user || !currentUser?.uid || !userData) return;

    const isSubscriber = hasPermission("ACCESS_CHAT_AREA");

    if (!isSubscriber) {
      setAlertMessage("Você precisa ser assinante para enviar mensagens.");
      setIsAlertOpen(true);
      return;
    }

    setChatLoading(true);

    try {
      await chatService.createOrGetConversation(
        currentUser.uid,
        userData,
        user.uid,
        user,
        isSubscriber,
      );
      navigate("/mensagens");
    } catch (err) {
      console.error("Erro ao iniciar chat:", err);
      alert("Não foi possível iniciar a conversa.");
    } finally {
      setChatLoading(false);
    }
  };

  const handleToggleFavorite = async () => {
    if (!user || !currentUser || !userData) return;

    const isSubscriber = hasPermission("LIKE_PROFILES");

    if (!isSubscriber) {
      setAlertMessage("Ops! Você precisa ser assinante para favoritar perfis.");
      setIsAlertOpen(true);
      return;
    }

    setFavoriteLoading(true);

    try {
      const newState = await interestsService.toggleFavorite(
        currentUser,
        userData,
        user,
        isSubscriber,
      );
      setIsFavorited(newState);
    } catch (err) {
      console.error("Erro ao favoritar:", err);
    } finally {
      setFavoriteLoading(false);
    }
  };

  const handleBlockUser = async () => {
    if (!user?.uid || !currentUser?.uid) return;

    if (!window.confirm(`Deseja bloquear ${user.username}?`)) return;

    setBlockLoading(true);
    try {
      await blockService.blockUser(currentUser.uid, user.uid, {
        username: user.username!,
        profileImage: profileImageUrl || null,
      });
      navigate(-1);
    } catch (err) {
      console.error("Erro ao bloquear:", err);
      alert("Não foi possível bloquear o usuário.");
    } finally {
      setBlockLoading(false);
    }
  };

  const handleShareClick = () => {
    setIsShareModalOpen(true);
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!username) return;
      setIsLoading(true);
      try {
        const profileData = await feedService.getProfileByUsername(username, {
          viewer: { uid: currentUser?.uid, gender: userData?.gender },
        });

        if (profileData) {
          setUser(profileData);
        }

        if (currentUser?.uid && profileData) {
          const favorited = await interestsService.checkIsFavorited(
            currentUser.uid,
            profileData,
          );
          setIsFavorited(favorited);

          const contactList = await contactsService.getContacts(profileData.id);

          setContacts(contactList);
        }
      } catch (err) {
        console.error("Erro ao carregar dados:", err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [username, currentUser?.uid]);

  useEffect(() => {
    if (
      user?.uid &&
      currentUser?.uid &&
      !isOwnProfile &&
      !viewRegistered &&
      userData?.username
    ) {
      feedService.registerView(currentUser.uid, user.uid, {
        username: userData.username,
        profileImage: profileImageUrl || null,
        city: userData.city || "",
        state: userData.state || "",
        birthYear: userData.birthYear || "",
        gender: userData.gender || "",
      });
      setViewRegistered(true);
    }
  }, [user, currentUser, isOwnProfile, userData, viewRegistered]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (moreRef.current && !moreRef.current.contains(e.target as Node)) {
        setIsMoreOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const statusRef = ref(rdb, `status/${user?.uid}`);

    const unsubscribe = onValue(statusRef, (snapshot) => {
      setPresence(snapshot.val());
    });

    return () => unsubscribe();
  }, [user?.uid]);

  if (isLoading)
    return (
      <div className="feed-user-page">
        <p className="feed-user-loading">Carregando perfil...</p>
      </div>
    );
  if (!user)
    return (
      <div className="feed-user-page">
        <p className="feed-user-empty">Perfil indisponível.</p>
      </div>
    );

  return (
    <div className="feed-user-page ">
      <PhotoGallery
        isOpen={isGalleryOpen}
        photos={galleryAllPhotos}
        initialIndex={galleryIndex}
        onClose={() => setIsGalleryOpen(false)}
        showNavigation={!isSinglePhotoMode}
      />

      <div
        className="feed-user-hero"
        style={{ backgroundImage: `url(${profileImageUrl})` }}
      >
        <div className="feed-user-hero-overlay" />
        <div className="feed-user-hero-content container">
          <div className="feed-user-identity">
            <div className="feed-user-name-row">
              <h1>
                {user.username},
                {age && <span className="feed-user-age">{age}</span>}
                {user?.isVerified && (
                  <span className="pill pill-verified">✔&#xFE0E;</span>
                )}
                {user?.planInfo?.active && (
                  <span className="pill pill-premium">★</span>
                )}
              </h1>
            </div>

            <p className="feed-user-location">
              {user.city}
              {user.city && user.state ? ", " : ""}
              {user.state}
            </p>

            <div className="feed-user-badges">
              <UserStatus
                state={presence?.state}
                lastActivity={presence?.lastActivity}
                showText={true}
              />
            </div>

            {user.bioTitle && (
              <p className="feed-user-tagline">“{user.bioTitle}”</p>
            )}

            <div className="feed-user-actions">
              {isOwnProfile ? (
                <>
                  <button
                    className="btn-primary"
                    onClick={() => navigate("/meu-perfil")}
                  >
                    Editar perfil
                  </button>

                  <button className="btn-primary" onClick={handleShareClick}>
                    Compartilhar
                  </button>

                  <ShareModal
                    isOpen={isShareModalOpen}
                    onClose={() => setIsShareModalOpen(false)}
                    uid={userData?.uid || ""}
                    usernameSlug={userData?.usernameSlug || ""}
                  />
                </>
              ) : (
                <>
                  <button
                    className="btn-primary"
                    onClick={handleStartChat}
                    disabled={chatLoading}
                  >
                    {chatLoading ? "Iniciando..." : "Enviar mensagem"}
                  </button>
                  <button
                    className="btn-secondary"
                    onClick={handleToggleFavorite}
                    disabled={favoriteLoading || !currentUser}
                  >
                    {favoriteLoading
                      ? "Salvando..."
                      : isFavorited
                        ? "Remover favorito"
                        : "Adicionar favorito"}
                  </button>

                  <div className="feed-user-more" ref={moreRef}>
                    <button
                      className="btn-ghost"
                      onClick={() => setIsMoreOpen(!isMoreOpen)}
                    >
                      <span className="feed-user-more-dots">⋮</span>
                    </button>
                    {isMoreOpen && (
                      <div className="feed-user-more-menu">
                        <button
                          className="feed-user-more-item"
                          onClick={handleBlockUser}
                          disabled={blockLoading}
                        >
                          {blockLoading ? "Bloqueando..." : "Bloquear"}
                        </button>
                        <button
                          className="feed-user-more-item disabled"
                          disabled
                          title="Em breve"
                        >
                          Denunciar
                        </button>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>

          <div
            className="avatar-wrapper"
            onClick={() => openGalleryAt(0)}
            style={{ cursor: "pointer" }}
          >
            <AvatarFeed
              src={profileImageUrl ?? undefined}
              alt={user.username}
            />
          </div>
        </div>
      </div>

      <div className="feed-user-container container">
        {user?.publicPhotos && user?.publicPhotos.length > 0 && (
          <section className="feed-user-section">
            <header className="section-header">
              <h2>Fotos</h2>
              <span className="muted">{user.photosCount || 0} públicas</span>
            </header>

            <div className="feed-user-photos">
              {user.publicPhotos.map((photoObj, idx) => (
                <div
                  className="feed-user-photo"
                  key={idx}
                  onClick={() => openGalleryAt(idx + galleryOffset)}
                >
                  <img
                    src={typeof photoObj === "string" ? photoObj : photoObj.url}
                    alt={`Foto ${idx + 1}`}
                    loading="lazy"
                  />
                </div>
              ))}
            </div>
          </section>
        )}

        {/* {contacts.length > 0 && (
          <section className="feed-user-section">
            <header className="section-header">
              <h2>Contatos</h2>
            </header>
            <div className="feed-user-contacts">
              {contacts.map((c, idx) => (
                <div key={idx} className="contact-item-row">
                  <span className="contact-label">
                    <strong>{c.name}:</strong>
                  </span>
                  <a href="#">Solicitar Acesso</a>
                </div>
              ))}
            </div>
          </section>
        )} */}

        <section className="feed-user-section">
          <header className="section-header">
            <h2>Sobre</h2>
          </header>
          <p className="feed-user-about">
            {user.bioDescription || "Sem descrição."}
          </p>
        </section>

        <section className="feed-user-section grid">
          <InfoGroup
            title="Informações Pessoais"
            items={[
              { label: "Estado civil", value: user.maritalStatus },
              { label: "Filhos", value: user.children },
              { label: "Buscando", value: user.seeking },
            ]}
          />
          <InfoGroup
            title="Aparência"
            items={[
              { label: "Altura", value: user.height },
              { label: "Corpo", value: user.bodyType },
              { label: "Pele", value: user.skinTone },
              { label: "Cabelo", value: user.hairColor },
              { label: "Olhos", value: user.eyeColor },
            ]}
          />
          <InfoGroup
            title="Estilo de Vida"
            items={[
              { label: "Fuma", value: user.smoking },
              { label: "Bebe", value: user.drinking },
            ]}
          />
          <InfoGroup
            title="Carreira"
            items={[
              { label: "Profissão", value: user.profession },
              { label: "Escolaridade", value: user.education },
            ]}
          />
        </section>
      </div>

      <ModalAlert
        title={alertMessage || "Ops!"}
        isOpen={isAlertOpen}
        onClose={() => setIsAlertOpen(false)}
        onSubscribe={() => {
          navigate("/planos");
          setIsAlertOpen(false);
        }}
      />
    </div>
  );
};

export default FeedUserScreen;
